import { plainToInstance } from 'class-transformer'

import { TabBody } from '@components/Tabs/components/TabBody'
import { BookEnum } from '@interfaces/BookEnum'
import SelectPerPage from '@components/Pagination/components/SelectPerPage/SelectPerPage'
import { StoryCard } from '@components/StoryCard'
import { OptionSelect } from '@components/OptionSelect/OptionSelect'
import { Pagination } from '@components/Pagination'
import StoryCardSkeleton from '@components/StoryCardSkeleton'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { BookStatusEnum } from '@interfaces/BookStatusEnum'
import { useAlert } from '@hooks/useAlert'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { NoBookIcon } from '@components/icons/NoBookIcon'
import { WritingTabProps } from './interface'

const bookTypeOptions = [
  { value: null, label: 'ทั้งหมด' },
  { value: BookEnum.NOVEL, label: 'นิยาย' },
  { value: BookEnum.MANGA, label: 'การ์ตูน' },
]

const perpageOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
]

export function WritingTab({
  data,
  penNameOptions,
  searchParam,
  page,
  perpage,
  total,
  isLoading,
  isLoadingToggleBookLike,
  handlePaginationChange,
  perpageChange,
  handlePenNameChange,
  handleBookTypeChange,
  handleLikeBook,
}: WritingTabProps) {
  const alert = useAlert()
  const storyDrawer = useDrawer({ drawer: 'story' })

  return (
    <TabBody tabId='writing'>
      <div className='border-t border-gray-3 dark:border-purple'>
        <div className='flex desktop-tablet:space-x-[10px] py-[20px] mobile:grid desktop:items-center mobile:gap-y-[20px]'>
          <OptionSelect
            className='w-[180px] mobile:w-full'
            title='ประเภท'
            value={searchParam.bookType}
            handleSelect={handleBookTypeChange}
            options={bookTypeOptions}
          />
          <OptionSelect
            className='w-[180px] mobile:w-full'
            title='นามปากกา'
            value={searchParam.penNameId}
            handleSelect={handlePenNameChange}
            options={[{ value: null, label: 'ทั้งหมด' }, ...penNameOptions]}
          />
          <div className='flex grow items-center justify-between'>
            <div className='text-secondary-1 text-sm font-light'>
              <span className='tablet-mobile:hidden'>จำนวนงานเขียน :</span>
              <span className='text-secondary dark:text-dark-10 font-bold desktop:ml-[4px]'>
                {total.toLocaleString()}
              </span>
              <span className='ml-[4px] tablet-mobile:ml-[10px]'>รายการ</span>
            </div>
            <SelectPerPage
              value={perpage}
              handleSelect={perpageChange}
              options={perpageOptions}
            />
          </div>
        </div>
        {isLoading ? (
          <div className='grid grid-cols-2 gap-[20px] mb-[30px] tablet:grid-cols-4 m-auto'>
            {[...Array(2)].map((l, i) => (
              <StoryCardSkeleton key={i} direction='horizontal' />
            ))}
          </div>
        ) : data?.length ? (
          <>
            <div
              data-test='writing-list'
              className='grid grid-cols-2 gap-[20px] mb-[30px] tablet:grid-cols-4 m-auto'
            >
              {data.map(item => (
                <StoryCard
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  isShowBookPromotion={item.hasSalePromotions}
                  buyAllChaptersPromotion={item.buyAllChaptersPromotion}
                  bookChapterCampaignHeader={item.bookChapterCampaignHeader}
                  readFreePromotion={item.readFreePromotion}
                  bookBadges={item.bookBadges}
                  imageUrl={item.coverResizeImgPath || item.coverImgPath}
                  penName={item.penName.firstPenName}
                  slug={item.penName.user.profilePageSlug}
                  content={item.cover}
                  category={item.category.name}
                  categoryId={item.category.id}
                  tags={item.tags}
                  chapter={item.chapterCount}
                  view={item.viewCount}
                  chat={item.commentCount}
                  like={item.likeCount}
                  isLiked={item.isLiked}
                  isEnded={item.isEnded}
                  bookType={item.bookType}
                  isToggleBookLike={isLoadingToggleBookLike}
                  handleLikeBook={handleLikeBook}
                  handleClickStoryCard={() => {
                    // TODO: ยังไม่ได้ดัก case ban
                    if (item.status === BookStatusEnum.DRAFT) {
                      alert.warning('ขออภัย เรื่องนี้ปิดการเผยแพร่แล้ว')
                    } else {
                      storyDrawer.show({
                        bookId: item.id,
                        initialData: plainToInstance(StoryDetailReaderType, {
                          id: item.id,
                          coverImgPath: item.coverImgPath,
                          title: item.title,
                          writingType: item.writingType,
                          publishedAt: item.publishedAt,
                          cover: item.cover,
                          viewCount: item.viewCount,
                          commentCount: item.commentCount,
                          likeCount: item.likeCount,
                          publishedChapterCount: item.chapterCount,
                          penName: item.penName,
                          category: item.category,
                          bookType: item.bookType,
                          tags: item.tags,
                          rating: {
                            name: '',
                          },
                          isEnded: item.isEnded,
                          isEpub: item.isEpub,
                          isPdf: item.isPdf,
                          isLiked: item.isLiked,
                        }),
                      })
                    }
                  }}
                />
              ))}
            </div>
            <Pagination
              page={page}
              perpage={perpage}
              total={total}
              pageChange={handlePaginationChange}
            />
          </>
        ) : (
          <div className='flex flex-col items-center justify-center pt-[40px]'>
            <NoBookIcon className='w-[120px] h-[119px] mb-[10px]' />
            <span className='font-mitr font-medium text-[18px] text-gray-12 dark:text-dark-10 mb-[31px]'>
              ไม่พบงานเขียน
            </span>
          </div>
        )}
      </div>
    </TabBody>
  )
}
